import React, {useCallback} from 'react';
import PropTypes from 'prop-types';
import lodashGet from 'lodash/get';
import cx from 'classnames';
import ActionButton from '@adsk/bim360-matrix-react-action-button';
import ProgressBar from '@adsk/bim360-matrix-react-progress-bar';
import {attachmentActions} from '../../constants/attachments';
import {attachmentAttributesType, issueAttributesType} from '../../constants/proptype-validation';
import i18n from '../../utils/i18n';
import trashIcon from '@adsk/bim360-matrix-react-assets/trash.svg';
import {canDeleteAttachment} from '../../utils/issues-helpers';
import IssueAttachmentThumbnail from './IssueAttachmentThumbnail';
import IssueAttachmentDescriptionContainer from './IssueAttachmentDescriptionContainer';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import {deleteAttachmentAction} from '../../app/actions';
import {
  getCurrentPreviewIssueAttributes,
  getCurrentPreviewIssueId,
  getCurrentUserId,
  getThumbnailUrlByAttachment
} from '../../app/selectors';
import {connect} from '../../app/connect';
import IssueAttachmentName from './IssueAttachmentName';
import useOpenAttachment from './useOpenAttachment';

const getOptions = attachment => {
  const options = [];

  if (canDeleteAttachment(attachment)) {
    options.push({
      key: attachmentActions.DELETE_ATTACHMENT,
      label: i18n.t('delete'),
      svg: trashIcon,
      width: '13px',
      height: '16px'
    });
  }

  return options;
};

const IssueAttachment = ({attachment, thumbnailUrl, container, currentUserId, className, issue, issueId, dispatch}) => {
  const isDeleting = lodashGet(attachment, 'isDeleting');
  const clickDisabled = lodashGet(attachment, 'isSaving') || lodashGet(attachment, 'isFailed') || isDeleting;
  const progressToShow = lodashGet(attachment, 'progress', 0);

  const openAttachment = useOpenAttachment({attachment, dispatch});

  const _handleAttachmentOptionSelection = useCallback(eventKey => {
    if (eventKey === attachmentActions.DELETE_ATTACHMENT) {
      const analyticsObj = {
        isOwner: attachment.createdBy === currentUserId,
        status: issue[ISSUE_ATTRIBUTE_NAMES.STATUS],
        assignedTo: issue[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO],
        assignedToType: issue[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE]
      };
      dispatch(deleteAttachmentAction({attachmentId: attachment.id, analyticsObj, issueId}));
    }
  }, [attachment, currentUserId, dispatch, issue, issueId]);

  return (
    <div
      className={cx('IssueAttachment', className, {
        'IssueAttachment--disabled': clickDisabled,
        'IssueAttachment--deleting': isDeleting
      })}
      onClick={clickDisabled ? null : openAttachment}
    >
      <div className="IssueAttachment__top" data-testid="IssueAttachment__top">
        <IssueAttachmentThumbnail thumbnailUrl={thumbnailUrl} />
        <div className="IssueAttachment__info" data-testid="IssueAttachment__info">
          <IssueAttachmentName attachment={attachment} container={container} />
          <IssueAttachmentDescriptionContainer attachment={attachment} container={container} />
        </div>
        {!clickDisabled && container && (
          <div className="IssueAttachment__actions" data-testid="IssueAttachment__actions">
            <ActionButton
              className="IssueAttachment__option-button"
              container={container}
              options={getOptions(attachment)}
              onSelect={_handleAttachmentOptionSelection}
            />
          </div>
        )}
      </div>
      {!!progressToShow && (
        <ProgressBar
          className={cx('IssueAttachment__progress', {
            'IssueAttachment__progress--upload-failed': attachment.isFailed
          })}
          max={100}
          now={progressToShow}
        />
      )}
    </div>
  );
};

IssueAttachment.displayName = 'IssueAttachment';

IssueAttachment.propTypes = {
  attachment: attachmentAttributesType.isRequired,
  thumbnailUrl: PropTypes.string,
  container: PropTypes.object.isRequired,
  currentUserId: PropTypes.string.isRequired,
  className: PropTypes.string,
  issue: issueAttributesType,
  issueId: PropTypes.string,
  dispatch: PropTypes.func.isRequired
};

const mapStateToProps = (state, ownProps) => ({
  thumbnailUrl: getThumbnailUrlByAttachment(state, ownProps.attachment),
  currentUserId: getCurrentUserId(state),
  issue: getCurrentPreviewIssueAttributes(state),
  issueId: getCurrentPreviewIssueId(state)
});

export default connect(mapStateToProps)(IssueAttachment);
